import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Checkbox } from "@shared/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { ArrowRight, MapPin } from "lucide-react";
import React, { useState } from "react";

interface NetworkData {
  id: string;
  name: string;
  parentSystem: string;
  assets: number;
  networkLength: string;
  conditionScore: number;
}

interface SuggestedNetwork {
  id: string;
  name: string;
  type: string;
  reason: string;
}

interface NetworkResult {
  id: string;
  name: string;
  type: string;
  serviceZone: string;
}

interface Facility {
  name: string;
  type?: string;
}

interface FilterData {
  search: string;
  parentSystem: string;
  conditionScore: string;
  networkLength: string;
}

interface SearchFilterData {
  networkType: string;
  serviceZone: string;
}

const getConditionColor = (score: number) => {
  if (score >= 80) return "bg-green-100 text-green-800";
  if (score >= 60) return "bg-yellow-100 text-yellow-800";
  return "bg-red-100 text-red-800";
};

const getTypeColor = (type: string) => {
  switch (type) {
    case "Water Main":
      return "bg-blue-100 text-blue-800";
    case "Sewer Main":
      return "bg-orange-100 text-orange-800";
    case "Force Main":
      return "bg-purple-100 text-purple-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const NetworkSuggestionsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  facilityName: string;
  onConnectSuggestions: (networkIds: string[]) => void;
  onSkipToSearch: () => void;
}> = ({
  isOpen,
  onClose,
  facilityName,
  onConnectSuggestions,
  onSkipToSearch,
}) => {
  const [selectedNetworks, setSelectedNetworks] = useState<string[]>([]);

  const suggestedNetworks: SuggestedNetwork[] = [
    {
      id: "NET-101",
      name: "Industrial Park Distribution Main",
      type: "Water Main",
      reason:
        "Serves the 'Industrial Park Zone', which is part of the 'Northern Division'.",
    },
    {
      id: "NET-102",
      name: "Hillside Residential Trunk Sewer",
      type: "Sewer Main",
      reason:
        "Serves the 'Hillside Zone', which is part of the 'Northern Division'.",
    },
    {
      id: "NET-103",
      name: "Downtown Core Water Loop",
      type: "Water Main",
      reason:
        "Serves the 'Downtown Core Zone', which is part of the 'Northern Division'.",
    },
  ];

  const handleNetworkToggle = (networkId: string) => {
    setSelectedNetworks((prev) =>
      prev.includes(networkId)
        ? prev.filter((id) => id !== networkId)
        : [...prev, networkId]
    );
  };

  const handleConnectSelected = () => {
    if (selectedNetworks.length > 0) {
      onConnectSuggestions(selectedNetworks);
      setSelectedNetworks([]);
    }
  };

  const handleClose = () => {
    setSelectedNetworks([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Network Suggestions for {facilityName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
            <MapPin className="h-4 w-4 text-blue-600" />
            <span>
              We found networks within this facility's service division. Select
              any to connect them.
            </span>
          </div>

          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Suggested Networks</h3>
            <div className="space-y-3">
              {suggestedNetworks.map((network) => (
                <div
                  key={network.id}
                  className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleNetworkToggle(network.id)}
                >
                  <Checkbox
                    id={network.id}
                    checked={selectedNetworks.includes(network.id)}
                    onCheckedChange={() => handleNetworkToggle(network.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor={network.id}
                        className="font-medium text-gray-900 cursor-pointer"
                      >
                        {network.name}
                      </label>
                      <Badge
                        variant="secondary"
                        className={getTypeColor(network.type)}
                      >
                        {network.type}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">
                      Reason: {network.reason}
                    </p>
                    <p className="text-sm text-gray-500">ID: {network.id}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4 border-t">
            <Button
              onClick={handleConnectSelected}
              disabled={selectedNetworks.length === 0}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Connect Selected Networks ({selectedNetworks.length})
            </Button>

            <Button
              variant="ghost"
              onClick={onSkipToSearch}
              className="w-full text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              Skip and Search All Networks
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>

            <Button variant="outline" onClick={handleClose} className="w-full">
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
